// A demo plan's own delete control: in the plan header, beside the `.demoMark` — see
// `PlanScreen.tsx` for where it is mounted.
//
// AN IN-PAGE CONFIRMATION, NOT `window.confirm`, which blocks the whole page and cannot
// be styled — the same reasoning `packages/web/operator/PlanDelete.tsx`'s own confirmation
// dialog gives, and the shape here borrows from it: a plain `aria-label`, Escape to
// dismiss, a scrim click that closes it, no focus trap and no typed confirmation, because
// neither exists anywhere else in this client either.
//
// THE SERVER IS THE ONLY GATE. This control renders for every demo plan and says nothing
// about who started it — nothing here could, since the caller's own account is not on the
// wire (see `contract.ts`'s `DemoSource`). A person invited into someone else's demo sees
// the same button, and trying it gets the server's ordinary refusal
// (`@mor/api`'s `routes.ts`), which reads here as the same generic "try again" every other
// failure in this client gets — this package enforces nothing, the same rule
// `client.ts`'s own header states for the whole client.

import { useEffect, useState } from 'react';

import { ApiFailure, type ApiClient } from '../api/client.js';

export type DemoDeleteControlProps = {
  client: ApiClient;
  planId: string;
  /** Called only once the server has actually removed the plan. */
  onDeleted: () => void;
};

export function DemoDeleteControl({
  client,
  planId,
  onDeleted,
}: DemoDeleteControlProps): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // Escape dismisses, matching `DecisionsMobileStrip`'s sheet and the operator dialog.
  useEffect(() => {
    if (!confirming) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setConfirming(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [confirming]);

  const remove = async (): Promise<void> => {
    setBusy(true);
    setProblem(null);
    try {
      await client.deleteDemoPlan(planId);
      onDeleted();
    } catch (error) {
      // The plan survives a refusal — this control only reports success through
      // `onDeleted` — and the dialog stays open so the message renders right next to the
      // button that caused it.
      setBusy(false);
      if (error instanceof ApiFailure && error.status === 404) {
        // Every refusal this route gives is a 404, indistinguishable from an unknown
        // address on purpose: not yours, not a demo, demo mode off. So the client cannot
        // say WHICH — but it can say the one thing that is true of all three, which is
        // that it will not start working. "Try again" was a lie about a permanent no, and
        // it sent a person round the same button until they gave up.
        setProblem('This demo can’t be deleted from here.');
        return;
      }
      setProblem('That didn’t delete. Try again.');
    }
  };

  return (
    <span className="demoDelete">
      <button
        type="button"
        className="demoDelete__button"
        aria-label="Delete this demo"
        onClick={() => setConfirming(true)}
      >
        Delete
      </button>
      {confirming ? (
        <div className="demoDelete__scrim" onClick={() => setConfirming(false)}>
          <div
            className="demoDelete__dialog"
            role="dialog"
            aria-label="Delete this demo"
            onClick={(event) => event.stopPropagation()}
          >
            <p>Delete this demo? This cannot be undone.</p>
            {problem === null ? null : (
              <p className="demoDelete__problem" role="status">
                {problem}
              </p>
            )}
            <div className="demoDelete__actions">
              <button type="button" disabled={busy} onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button type="button" disabled={busy} onClick={() => void remove()}>
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </span>
  );
}
